import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ArrowLeft, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryNavigation = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [typeProducts, setTypeProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchProductTypes();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSelectedCategory(null);
      setSelectedType(null);
      setBreadcrumbs([]);
      setTypeProducts({});
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/categories/hierarchy`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    setTypesLoading(true);
    try {
      const response = await axios.get(`${API}/product-types`, {
        params: { is_active: true }
      });
      setProductTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch product types:', error);
    } finally {
      setTypesLoading(false);
    }
  };

  const fetchTypeProducts = async (category, type) => {
    const key = `${category.code}-${type.code}`;
    if (typeProducts[key] || loadingProducts[key]) return;

    setLoadingProducts(prev => ({ ...prev, [key]: true }));
    try {
      const response = await axios.get(`${API}/products/preview`, {
        params: { 
          category: category.code, 
          type: type.code, 
          limit: 4 
        }
      });
      
      if (response.data.success) {
        setTypeProducts(prev => ({ 
          ...prev, 
          [key]: response.data.products 
        }));
      }
    } catch (error) {
      console.error('Failed to fetch type products:', error);
      // Don't show error toast for preview images, fail silently
    } finally {
      setLoadingProducts(prev => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    if (selectedCategory && productTypes.length > 0) {
      // Prefetch products for all types when category is selected
      productTypes.forEach(type => {
        fetchTypeProducts(selectedCategory, type);
      });
    }
  }, [selectedCategory, productTypes]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setBreadcrumbs([{ name: category.name, type: 'category' }]);
  };

  const handleTypeClick = (type) => {
    // Navigate directly to shop page with category and type filters
    handleNavigateToShop(selectedCategory, type);
  };

  const handleNavigateToShop = (category, type) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category.code);
    if (type) params.append('type', type.code);
    
    navigate(`/shop?${params.toString()}`);
    onClose();
  };

  const handleBack = () => {
    if (selectedType) {
      setSelectedType(null);
      setBreadcrumbs([{ name: selectedCategory.name, type: 'category' }]);
    } else if (selectedCategory) {
      setSelectedCategory(null);
      setBreadcrumbs([]);
    }
  };

  const handleBreadcrumbClick = (index) => {
    if (index === 0 && breadcrumbs.length > 1) {
      setSelectedType(null);
      setBreadcrumbs([breadcrumbs[0]]);
    } else if (index === -1) {
      setSelectedCategory(null);
      setSelectedType(null);
      setBreadcrumbs([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div 
        className="h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-black/60 border-b border-white/10 px-4 sm:px-6 py-4 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              {(selectedCategory || selectedType) && (
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-white/10 rounded-lg transition"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {selectedType ? selectedType.name : selectedCategory ? selectedCategory.name : 'Select a Category'}
                </h2>
                
                {/* Breadcrumbs */}
                {breadcrumbs.length > 0 && (
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                    <button
                      onClick={() => handleBreadcrumbClick(-1)}
                      className="hover:text-white transition"
                    >
                      Categories
                    </button>
                    {breadcrumbs.map((crumb, index) => (
                      <React.Fragment key={index}>
                        <ChevronRight size={14} />
                        <button
                          onClick={() => handleBreadcrumbClick(index)}
                          className={`hover:text-white transition ${
                            index === breadcrumbs.length - 1 ? 'text-white font-semibold' : ''
                          }`}
                        >
                          {crumb.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full max-w-7xl mx-auto flex">
            {/* Left Sidebar - Category List */}
            <div className="w-64 border-r border-white/10 bg-black/20 backdrop-blur-lg overflow-y-auto hidden sm:block">
              <div className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">
                  Categories
                </h3>
                <div className="space-y-1">
                  {loading ? (
                    <div className="space-y-2">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="h-10 bg-white/5 rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition flex items-center justify-between group ${
                          selectedCategory?.id === category.id
                            ? 'bg-red-600 text-white'
                            : 'hover:bg-white/10 text-gray-300'
                        }`}
                      >
                        <span className="text-sm font-medium">{category.name}</span>
                        <ChevronRight 
                          size={16} 
                          className={`transition-transform ${
                            selectedCategory?.id === category.id ? '' : 'group-hover:translate-x-1'
                          }`}
                        />
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Content Area - Cards Grid */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="aspect-square bg-white/5 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : !selectedCategory ? (
                // Main Categories Grid
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category)}
                      className="group relative aspect-square bg-gradient-to-br from-white/5 to-white/10 rounded-xl overflow-hidden border border-white/10 hover:border-red-500 transition-all hover:scale-105"
                    >
                      {category.image_url ? (
                        <img
                          src={category.image_url}
                          alt={category.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Package size={48} className="text-white/20" />
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent" />
                      
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-bold text-lg mb-1">{category.name}</h3>
                        <p className="text-gray-300 text-xs">
                          {category.product_count} {category.product_count === 1 ? 'item' : 'items'}
                        </p>
                      </div>
                      
                      <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight size={20} className="text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : !selectedType ? (
                // Product Types Grid with Image Mosaics
                <div>
                  <div className="mb-6">
                    <button
                      onClick={() => handleNavigateToShop(selectedCategory, null)}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      View All {selectedCategory.name} ({selectedCategory.product_count})
                    </button>
                  </div>
                  
                  {typesLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="aspect-square bg-white/5 rounded-xl animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    (() => {
                      // Only show types that have at least one product in this category.
                      // While a type's products are still being fetched, keep it visible
                      // as a loading skeleton rather than hiding it prematurely.
                      const visibleTypes = productTypes.filter(type => {
                        const key = `${selectedCategory.code}-${type.code}`;
                        const products = typeProducts[key] || [];
                        const stillLoading = loadingProducts[key] !== false;
                        return stillLoading || products.length > 0;
                      });

                      if (visibleTypes.length === 0) {
                        return (
                          <p className="text-gray-400 text-sm">No product types available yet.</p>
                        );
                      }

                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {visibleTypes.map((type) => {
                            const key = `${selectedCategory.code}-${type.code}`;
                            const products = typeProducts[key] || [];
                            const isLoading = loadingProducts[key] !== false && !(key in typeProducts);

                            return (
                              <button
                                key={type.code}
                                onClick={() => handleTypeClick(type)}
                                className="group relative aspect-square bg-gradient-to-br from-white/5 to-white/10 rounded-xl overflow-hidden border border-white/10 hover:border-red-500 transition-all hover:scale-105"
                              >
                                {/* Product Images */}
                                {isLoading ? (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                  </div>
                                ) : products.length === 1 ? (
                                  // Single product - fill the whole card with that one image
                                  <div className="absolute inset-0">
                                    {products[0].primary_image ? (
                                      <img
                                        src={products[0].primary_image}
                                        alt={products[0].name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-white/5">
                                        <Package size={32} className="text-white/30" />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  // Multiple products - 2x2 mosaic
                                  <div className="absolute inset-0 grid grid-cols-2 gap-1 p-2">
                                    {products.slice(0, 4).map((product) => (
                                      <div
                                        key={product.id}
                                        className="relative overflow-hidden rounded-lg bg-white/5"
                                      >
                                        {product.primary_image ? (
                                          <img
                                            src={product.primary_image}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Package size={20} className="text-white/30" />
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {/* Fill remaining slots if fewer than 4 products */}
                                    {[...Array(Math.max(0, 4 - products.length))].map((_, idx) => (
                                      <div
                                        key={`empty-${idx}`}
                                        className="relative overflow-hidden rounded-lg bg-white/5 flex items-center justify-center"
                                      >
                                        <Package size={20} className="text-white/20" />
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                                {/* Type Name */}
                                <div className="absolute bottom-0 left-0 right-0 p-3">
                                  <h3 className="text-white font-bold text-sm">{type.name}</h3>
                                  {products.length > 0 && (
                                    <p className="text-gray-300 text-xs mt-1">
                                      {products.length}+ items
                                    </p>
                                  )}
                                </div>

                                {/* Hover Arrow */}
                                <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <ChevronRight size={16} className="text-white" />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
              ) : (
                // Final Selection - Navigate to Shop
                <div className="flex flex-col items-center justify-center h-full max-w-md mx-auto text-center">
                  <Package size={64} className="text-white/30 mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {selectedCategory.name} - {selectedType.name}
                  </h3>
                  <p className="text-gray-400 mb-8">
                    Browse all {selectedType.name.toLowerCase()} from {selectedCategory.name}
                  </p>
                  <button
                    onClick={() => handleNavigateToShop(selectedCategory, selectedType)}
                    className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors text-lg"
                  >
                    Browse Products
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryNavigation;